import { useQuery } from '@apollo/client';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search as SearchIcon,
  Lock as LockIcon,
  Key as KeyIcon,
  Star as StarIcon,
  Add as AddIcon,
  Share as ShareIcon,
  StickyNote2 as NoteIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  Fab,
  Alert,
  Skeleton,
} from '@mui/material';

import { VAULT_ITEMS_QUERY } from '../services/graphql';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type {
  EncryptedVaultItem,
  DecryptedVaultItem,
  VaultItemType,
  PasswordPayload,
  CreditCardPayload,
  ContactPayload,
} from '../types';
import { decryptItemKey, decryptPayload, decryptItemKeyFromSharing } from '../crypto/encryption';
import { base64ToBytes } from '../crypto/kdf';

export default function Vault() {
  const navigate = useNavigate();
  const { vaultKey, privateKey } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [decryptedItems, setDecryptedItems] = useState<DecryptedVaultItem[]>([]);
  const [decryptError, setDecryptError] = useState<string | null>(null);

  const workspaceId = currentWorkspace?.id;

  // Query vault items for the current workspace
  const { data, loading, error, refetch } = useQuery(VAULT_ITEMS_QUERY, {
    variables: { workspaceId },
    skip: !workspaceId, // Don't query until workspace is selected
    fetchPolicy: 'cache-and-network',
  });

  // Refetch when workspace changes
  useEffect(() => {
    if (workspaceId) {
      refetch({ workspaceId });
      setDecryptedItems([]); // Clear items when switching workspace
    }
  }, [workspaceId, refetch]);

  // Decrypt items when data changes
  useEffect(() => {
    if (!data?.vaultItems || !vaultKey) {
      return;
    }

    const decryptItems = async () => {
      try {
        const items: DecryptedVaultItem[] = [];

        for (const item of data.vaultItems as EncryptedVaultItem[]) {
          let itemKey: Uint8Array | null = null;

          if (item.isOwner) {
            // For owned items, decrypt with vault key (symmetric)
            itemKey = decryptItemKey(item.itemKeyEncrypted, item.itemKeyNonce, vaultKey);
          } else {
            // For shared items, decrypt with X25519 key exchange
            if (!privateKey || !item.ownerPublicKey) {
              console.error('Missing keys for shared item:', item.id);
              continue;
            }
            const ownerPublicKeyBytes = base64ToBytes(item.ownerPublicKey);
            itemKey = decryptItemKeyFromSharing(
              item.itemKeyEncrypted,
              item.itemKeyNonce,
              ownerPublicKeyBytes,
              privateKey
            );
          }

          if (!itemKey) {
            console.error('Failed to decrypt item key for item:', item.id);
            continue;
          }

          // Decrypt payload
          const payload = decryptPayload(item.encryptedPayload, item.payloadNonce, itemKey);

          if (!payload) {
            console.error('Failed to decrypt payload for item:', item.id);
            continue;
          }

          items.push({
            id: item.id,
            type: item.type,
            payload,
            isFavorite: item.isFavorite,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            isOwner: item.isOwner,
            ownerId: item.ownerId,
            ownerEmail: item.ownerEmail,
            ownerPublicKey: item.ownerPublicKey,
            owner: item.owner,
            shares: item.shares || [],
            itemKey,
          });
        }

        setDecryptedItems(items);
        setDecryptError(null);
      } catch (err) {
        setDecryptError('Failed to decrypt vault items');
        console.error('Decryption error:', err);
      }
    };

    decryptItems();
  }, [data, vaultKey, privateKey]);

  // Filter items based on search and tab
  const filteredItems = useMemo(() => {
    let items = decryptedItems;

    // Filter by type based on tab
    if (activeTab === 1) {
      items = items.filter((item) => item.type === 'password');
    } else if (activeTab === 2) {
      items = items.filter((item: DecryptedVaultItem) => item.type === 'totp');
    } else if (activeTab === 3) {
      items = items.filter(
        (item) =>
          item.type === 'secure_note' || item.type === 'credit_card' || item.type === 'contact'
      );
    } else if (activeTab === 4) {
      items = items.filter((item: DecryptedVaultItem) => item.isFavorite);
    } else if (activeTab === 5) {
      items = items.filter((item: DecryptedVaultItem) => !item.isOwner);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter((item: DecryptedVaultItem) => {
        const title = item.payload.title.toLowerCase();
        const hasUsername =
          item.payload.type === 'password' &&
          item.payload.username?.toLowerCase().includes(query);
        const hasCardNumber =
          item.payload.type === 'credit_card' &&
          item.payload.cardNumber?.replace(/\s/g, '').includes(query.replace(/\s/g, ''));
        const hasContactName =
          item.payload.type === 'contact' &&
          item.payload.fullName?.toLowerCase().includes(query);

        return title.includes(query) || hasUsername || hasCardNumber || hasContactName;
      });
    }

    return items;
  }, [decryptedItems, activeTab, searchQuery]);

  const getItemIcon = (type: VaultItemType) => {
    switch (type) {
      case 'totp':
        return <KeyIcon />;
      case 'secure_note':
        return <NoteIcon />;
      case 'credit_card':
        return <CreditCardIcon />;
      case 'contact':
        return <PersonIcon />;
      default:
        return <LockIcon />;
    }
  };

  const getItemColor = (type: VaultItemType) => {
    switch (type) {
      case 'totp':
        return 'secondary.light';
      case 'secure_note':
        return '#8B5CF6';
      case 'credit_card':
        return '#F59E0B';
      case 'contact':
        return '#10B981';
      default:
        return 'primary.light';
    }
  };

  const getItemSubtitle = (item: DecryptedVaultItem) => {
    const payload = item.payload;
    switch (payload.type) {
      case 'password': {
        const pwPayload = payload as PasswordPayload;
        if (pwPayload.websites?.length && pwPayload.websites[0]) {
          try {
            const urlStr = pwPayload.websites[0];
            const url = new URL(urlStr.startsWith('http') ? urlStr : `https://${urlStr}`);
            return url.hostname;
          } catch {
            return pwPayload.websites[0];
          }
        }
        return pwPayload.username || 'Password';
      }
      case 'totp':
        return '2FA Code';
      case 'secure_note':
        return 'Secure Note';
      case 'credit_card': {
        const ccPayload = payload as CreditCardPayload;
        const last4 = ccPayload.cardNumber.replace(/\D/g, '').slice(-4);
        return last4 ? `•••• ${last4}` : 'Credit Card';
      }
      case 'contact': {
        const contactPayload = payload as ContactPayload;
        return contactPayload.email || contactPayload.phone || 'Contact';
      }
      default:
        return '';
    }
  };

  const getOwnerLabel = (item: DecryptedVaultItem) => {
    if (item.isOwner) {
      return 'Me';
    }
    return item.ownerEmail || 'Unknown';
  };

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">Failed to load vault items</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Your Vault
        </Typography>

        <TextField
          fullWidth
          placeholder="Search vault items..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          <Tab label="All" />
          <Tab label="Passwords" />
          <Tab label="2FA" />
          <Tab label="Other" />
          <Tab label="Favorites" icon={<StarIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Shared" icon={<ShareIcon fontSize="small" />} iconPosition="start" />
        </Tabs>

        {decryptError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {decryptError}
          </Alert>
        )}

        {loading && !decryptedItems.length ? (
          <List>
            {[1, 2, 3].map((i) => (
              <ListItem key={i} disablePadding sx={{ mb: 1 }}>
                <Skeleton variant="rounded" width="100%" height={72} />
              </ListItem>
            ))}
          </List>
        ) : filteredItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <LockIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {searchQuery ? 'No items found' : 'Your vault is empty'}
            </Typography>
            {!searchQuery && (
              <Typography variant="body2" color="text.secondary">
                Add your first item to get started
              </Typography>
            )}
          </Box>
        ) : (
          <List>
            {filteredItems.map((item: DecryptedVaultItem) => (
              <ListItem
                key={item.id}
                disablePadding
                sx={{ mb: 1 }}
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!item.isOwner && (
                      <Chip
                        size="small"
                        label={getOwnerLabel(item)}
                        variant="outlined"
                        color="info"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    )}
                    {item.shares.length > 0 && (
                      <Chip
                        size="small"
                        icon={<ShareIcon fontSize="small" />}
                        label={item.shares.length}
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
              >
                <ListItemButton
                  onClick={() => navigate(`/vault/${item.id}`)}
                  sx={{
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: getItemColor(item.type),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      {getItemIcon(item.type)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.payload.title}
                        {item.isFavorite && <StarIcon fontSize="small" color="warning" />}
                      </Box>
                    }
                    secondary={getItemSubtitle(item)}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Container>

      <Fab
        color="primary"
        aria-label="add"
        onClick={() => navigate('/add')}
        sx={{
          position: 'fixed',
          bottom: { xs: 80, md: 24 },
          right: 24,
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
}
